/**
 * 教师跟场评分的内存 store（不持久化，不依赖后端）。
 *
 * 教师端与复盘台共用同一个模块级 store：教练台录入分数后切换到复盘台，
 * 导出战报时仍能带上评分。页面卸载不会丢（模块级），刷新页面会重置（符合
 * 「Phase 1 不要求后端持久化」的约束）。
 */
import { useSyncExternalStore } from 'react';

export type CoachScoreMap = Record<string, Record<string, number>>;

let scores: CoachScoreMap = {};
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

export function getCoachScores(): CoachScoreMap {
  return scores;
}

export function subscribeCoachScores(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** 写入单个维度分数（1~5；传 0 / undefined 表示清除该维度） */
export function setCoachScore(participantId: string, dimensionId: string, value: number): void {
  const next = { ...scores };
  const entry = { ...(next[participantId] ?? {}) };
  if (!value) {
    delete entry[dimensionId];
  } else {
    entry[dimensionId] = value;
  }
  if (Object.keys(entry).length === 0) {
    delete next[participantId];
  } else {
    next[participantId] = entry;
  }
  scores = next;
  emit();
}

export function clearCoachScores(): void {
  scores = {};
  emit();
}

/** React hook：订阅评分表 */
export function useCoachScores(): CoachScoreMap {
  return useSyncExternalStore(subscribeCoachScores, getCoachScores, getCoachScores);
}

/** 测试辅助：同步重置（不触发订阅） */
export function __resetCoachScoresForTests(): void {
  clearCoachScores();
}
